import { NONNULL_PRIORITY_OPTIONS } from "~/constants/priority";
import { RELATIONSHIP_KIND_KIND_OPTIONS } from "~/constants/relationship";
import { USER_KIND_DART_AI } from "~/constants/userKinds";
import { DartboardKind, Priority, RelationshipKindKind, StatusKind, UserStatus } from "~/shared/enums";
import type {
  AgentMemory,
  AgentSchedule,
  Attachment,
  Automation,
  Chat,
  ChatMessage,
  Comment,
  Comparator,
  ComparatorResult,
  Dartboard,
  Dashboard,
  DateRange,
  Doc,
  Folder,
  Form,
  FormField,
  Layout,
  Notification,
  Option,
  Position,
  Property,
  PropertyConfig,
  RelationshipKind,
  Skill,
  Space,
  Status,
  Task,
  TaskDocRelationship,
  TaskKind,
  TextSnapshot,
  TimeTracking,
  TimeTrackingEntry,
  User,
  View,
  Webhook,
} from "~/shared/types";
import { getAncestorDuids, simpleStrHash } from "~/utils/common";
import { getTimeTrackingDuration } from "~/utils/time";

const DARTBOARD_SORT_ORDER = Object.values(DartboardKind);

export const STATUS_SORT_ORDER = Object.values(StatusKind);
const PRIORITY_SORT_ORDER = [null, ...NONNULL_PRIORITY_OPTIONS.toReversed()];
const RELATIONSHIP_KIND_KIND_SORT_ORDER = [...RELATIONSHIP_KIND_KIND_OPTIONS];

const USER_STATUS_TO_INDEX = Object.fromEntries(
  [
    UserStatus.ACTIVE,
    UserStatus.AGENT_CONFIG_ERROR,
    UserStatus.PENDING_EMAIL_VERIFICATION,
    UserStatus.INVITED,
    UserStatus.PENDING_SUBSCRIPTION_UPGRADE,
    UserStatus.DEACTIVATED,
    UserStatus.AGENT_DISABLED,
  ].map((e, i) => [e, i])
);

// TODO use in more of the other comparators below
export const makeWithUndefinedLast =
  <T>(comparator: Comparator<T>) =>
  (a: T | undefined, b: T | undefined) => {
    if (a === undefined || b === undefined) {
      return a === b ? 0 : b === undefined ? -1 : 1;
    }
    return comparator(a, b);
  };

export const sign = (n: number): ComparatorResult => (n === 0 ? 0 : n > 0 ? 1 : -1);

export const booleanComparator = (a: boolean, b: boolean): ComparatorResult => (a === b ? 0 : a ? -1 : 1);

export const numberComparator = (a: number, b: number): ComparatorResult => sign(a - b);

export const stringComparator = (a: string, b: string): ComparatorResult => (a > b ? 1 : b > a ? -1 : 0);

export const stringComparatorReversed = (a: string, b: string): ComparatorResult => (a > b ? -1 : b > a ? 1 : 0);

export const makeStringComparator = <T>(getStrFn: (object: T) => string, reverse = false) => {
  if (reverse) {
    return (a: T, b: T): ComparatorResult => stringComparatorReversed(getStrFn(a), getStrFn(b));
  }
  return (a: T, b: T): ComparatorResult => stringComparator(getStrFn(a), getStrFn(b));
};

export const makeListComparatorOrganizedBySize =
  <T>(comparator: (a: T, b: T) => ComparatorResult) =>
  (a: T[], b: T[]): ComparatorResult => {
    const aLen = a.length;
    const bLen = b.length;
    for (let i = 0; i < aLen; i += 1) {
      if (i >= bLen) {
        return 1;
      }
      const diff = comparator(a[i], b[i]);
      if (diff !== 0) {
        return diff;
      }
    }
    return aLen === bLen ? 0 : -1;
  };

export const stringListComparator = makeListComparatorOrganizedBySize(stringComparator);

export const notificationComparator = (a: Notification, b: Notification): ComparatorResult =>
  stringComparator(b.createdAt, a.createdAt);

export const textSnapshotComparator = (a: TextSnapshot, b: TextSnapshot): ComparatorResult =>
  stringComparator(b.createdAt, a.createdAt);

export const spaceComparator = (a: Space | undefined, b: Space | undefined) => {
  if (!a || !b) {
    return a === b ? 0 : a ? -1 : 1;
  }
  return stringComparator(a.order, b.order);
};

export const makeDartboardComparator =
  (
    getSpaceFn: (spaceDuid: string) => Space | undefined,
    options: { chronologicalOrder: boolean } = { chronologicalOrder: false }
  ) =>
  (a: Dartboard | undefined, b: Dartboard | undefined): ComparatorResult => {
    if (!a || !b) {
      return a === b ? 0 : a ? -1 : 1;
    }

    const spaceDiff = spaceComparator(getSpaceFn(a.spaceDuid), getSpaceFn(b.spaceDuid));
    if (spaceDiff !== 0) {
      return spaceDiff;
    }
    const sortOrder = options.chronologicalOrder
      ? DARTBOARD_SORT_ORDER.toSorted((c, d) =>
          c === DartboardKind.FINISHED ? -1 : d === DartboardKind.FINISHED ? 1 : 0
        )
      : DARTBOARD_SORT_ORDER;
    const indexDiff = sortOrder.indexOf(a.kind) - sortOrder.indexOf(b.kind);
    if (indexDiff !== 0) {
      return sign(indexDiff);
    }
    if (a.kind === DartboardKind.FINISHED) {
      return options.chronologicalOrder ? sign((a.index ?? 0) - (b.index ?? 0)) : sign((b.index ?? 0) - (a.index ?? 0));
    }
    return stringComparator(a.order, b.order);
  };

export const priorityComparator = (a: Priority | null, b: Priority | null): ComparatorResult => {
  if (!a || !b) {
    return a === b ? 0 : a ? -1 : 1;
  }
  return sign(PRIORITY_SORT_ORDER.indexOf(a) - PRIORITY_SORT_ORDER.indexOf(b));
};
export const dateComparator = (a: string | null, b: string | null): ComparatorResult => {
  if (!a || !b) {
    return a === b ? 0 : a ? -1 : 1;
  }
  return stringComparator(a, b);
};

export const datesComparator = (a: DateRange, b: DateRange): ComparatorResult => {
  const [aStart, aDue] = a;
  const [bStart, bDue] = b;
  return dateComparator(aStart, bStart) || dateComparator(aDue, bDue);
};

export const timeTrackingEntryComparator = (a: TimeTrackingEntry, b: TimeTrackingEntry): ComparatorResult => {
  const aFin = a.finishedAt;
  const bFin = b.finishedAt;
  if ((!aFin || !bFin) && aFin !== bFin) {
    return aFin ? -1 : 1;
  }
  const startDiff = stringComparator(a.startedAt, b.startedAt);
  if (startDiff !== 0) {
    return startDiff;
  }
  if (!aFin || !bFin) {
    return 0;
  }
  return stringComparator(aFin, bFin);
};

export const timeTrackingComparator = (a: TimeTracking, b: TimeTracking): ComparatorResult => {
  const aMs = getTimeTrackingDuration(a).asMilliseconds();
  const bMs = getTimeTrackingDuration(b).asMilliseconds();
  return sign(aMs - bMs);
};

export const makeDateOrDatesComparator = (isRange: boolean) =>
  isRange ? datesComparator : (a: DateRange, b: DateRange) => dateComparator(a[1], b[1]);

export const sizeComparator = (a: number | null, b: number | null) => {
  if (!a || !b) {
    return a === b ? 0 : a ? -1 : 1;
  }
  return sign(a - b);
};

export const attachmentComparator = (a: Attachment, b: Attachment): ComparatorResult =>
  stringComparator(a.order, b.order);

export const propertyComparator = (a: Property, b: Property): ComparatorResult => stringComparator(a.order, b.order);

export const propertyObjectComparator = (a: [Property, PropertyConfig], b: [Property, PropertyConfig]) => {
  const aIsDefault = a[1].isDefault;
  const bIsDefault = b[1].isDefault;
  if (aIsDefault !== bIsDefault) {
    return aIsDefault ? -1 : 1;
  }
  return propertyComparator(a[0], b[0]);
};

export const taskKindComparator = (a: TaskKind | undefined, b: TaskKind | undefined): ComparatorResult => {
  if (!a || !b) {
    return a === b ? 0 : a ? -1 : 1;
  }
  return stringComparator(a.order, b.order);
};

export const checkboxComparator = booleanComparator;

export const statusComparator = (a: Status | undefined, b: Status | undefined): ComparatorResult => {
  if (!a || !b) {
    return a === b ? 0 : a ? -1 : 1;
  }
  const diff = STATUS_SORT_ORDER.indexOf(a.kind) - STATUS_SORT_ORDER.indexOf(b.kind);
  if (diff !== 0) {
    return sign(diff);
  }

  return stringComparator(a.order, b.order);
};

export const numberMaybeComparator = (a: number | null, b: number | null) => {
  if (!a || !b) {
    return a === b ? 0 : a ? -1 : 1;
  }
  return sign(a - b);
};

export const optionComparator = (a: Option | undefined, b: Option | undefined): ComparatorResult => {
  if (!a || !b) {
    return a === b ? 0 : a ? -1 : 1;
  }
  return stringComparator(a.title, b.title);
};

export const makeHierarchicalOptionComparator = (allOptions: Option[]) => {
  const sortedOptions = [...allOptions].sort((a, b) => stringComparator(a.title, b.title));
  const optionIndexMap = new Map(sortedOptions.map((option, index) => [option.duid, index]));

  return (a: Option, b: Option): ComparatorResult => {
    const aPath = [...getAncestorDuids(allOptions, a), a.duid].map(
      (duid) => optionIndexMap.get(duid) ?? sortedOptions.length
    );
    const bPath = [...getAncestorDuids(allOptions, b), b.duid].map(
      (duid) => optionIndexMap.get(duid) ?? sortedOptions.length
    );

    return makeListComparatorOrganizedBySize(numberComparator)(aPath, bPath);
  };
};

export const optionsComparator = (a: Option[], b: Option[]) =>
  makeListComparatorOrganizedBySize(optionComparator)(a, b);

export const formComparator = (a: Form, b: Form): ComparatorResult => stringComparator(a.order, b.order);

export const formFieldComparator = (a: FormField, b: FormField): ComparatorResult => stringComparator(a.order, b.order);

export const makeTaskComparator =
  (
    getSpaceFn: (spaceDuid: string) => Space | undefined,
    getDartboardFn: (dartboardDuid: string) => Dartboard | undefined
  ) =>
  (a: Task | undefined, b: Task | undefined): ComparatorResult => {
    if (!a || !b) {
      return a === b ? 0 : a ? -1 : 1;
    }
    if (a.inTrash !== b.inTrash) {
      return a.inTrash ? 1 : -1;
    }
    const dartboardDiff = makeDartboardComparator(getSpaceFn)(
      getDartboardFn(a.dartboardDuid),
      getDartboardFn(b.dartboardDuid)
    );
    if (dartboardDiff !== 0) {
      return dartboardDiff;
    }

    return stringComparator(a.order, b.order);
  };

export const makeTaskComparatorWithDisabledAndCurrentDartboard = (
  getSpaceFn: (spaceDuid: string) => Space | undefined,
  getDartboardFn: (dartboardDuid: string) => Dartboard | undefined,
  getDisabledFn: (task: Task) => boolean,
  currentDartboard: Dartboard | undefined
) => {
  const standardTaskComparator = makeTaskComparator(getSpaceFn, getDartboardFn);
  return (a: Task, b: Task) => {
    const aDisabled = getDisabledFn(a);
    const bDisabled = getDisabledFn(b);
    if (aDisabled !== bDisabled) {
      return aDisabled ? 1 : -1;
    }

    const aInCurrentDartboard = a.dartboardDuid === currentDartboard?.duid;
    const bInCurrentDartboard = b.dartboardDuid === currentDartboard?.duid;
    if (aInCurrentDartboard !== bInCurrentDartboard) {
      return aInCurrentDartboard ? -1 : 1;
    }
    return standardTaskComparator(a, b);
  };
};

export const userComparator = (a: User | undefined, b: User | undefined): ComparatorResult => {
  if (!a || !b) {
    return a === b ? 0 : a ? -1 : 1;
  }
  // Sort by status, then whether they have a name, then alphabetical
  const statusDiff = USER_STATUS_TO_INDEX[a.status] - USER_STATUS_TO_INDEX[b.status];
  if (statusDiff !== 0) {
    return sign(statusDiff);
  }

  if (!a.name || !b.name) {
    return a.name === b.name ? stringComparator(a.email, b.email) : a.name ? -1 : 1;
  }
  return stringComparator(a.name.toLowerCase(), b.name.toLowerCase());
};

export const automationComparator = (a: Automation, b: Automation): ComparatorResult =>
  stringComparator(a.order, b.order);

export const agentComparator = (a: User, b: User): ComparatorResult => {
  const aIsDartAi = a.kind === USER_KIND_DART_AI;
  const bIsDartAi = b.kind === USER_KIND_DART_AI;
  if (aIsDartAi !== bIsDartAi) {
    return aIsDartAi ? -1 : 1;
  }

  // Sort by Dart AI, then no-names first, then alphabetical
  if (!a.name || !b.name) {
    return a.name === b.name ? stringComparator(a.duid, b.duid) : a.name ? 1 : -1;
  }
  return stringComparator(a.name.toLowerCase(), b.name.toLowerCase());
};

export const usersComparator = (a: User[], b: User[]) => makeListComparatorOrganizedBySize(userComparator)(a, b);

export const viewComparator = (a: View, b: View): ComparatorResult => stringComparator(a.order, b.order);

export const webhookComparator = (a: Webhook, b: Webhook): ComparatorResult => stringComparator(a.order, b.order);

export const makeFolderComparator =
  (getSpaceFn: (spaceDuid: string) => Space | undefined) => (a: Folder | undefined, b: Folder | undefined) => {
    if (!a || !b) {
      return a === b ? 0 : a ? -1 : 1;
    }
    const spaceDiff = spaceComparator(getSpaceFn(a.spaceDuid), getSpaceFn(b.spaceDuid));
    if (spaceDiff !== 0) {
      return spaceDiff;
    }
    return stringComparator(a.order, b.order);
  };

export const dashboardComparator = (a: Dashboard, b: Dashboard): ComparatorResult => stringComparator(a.order, b.order);

export const makeDocComparator =
  (getSpaceFn: (spaceDuid: string) => Space | undefined, getFolderFn: (folderDuid: string) => Folder | undefined) =>
  (a: Doc | undefined, b: Doc | undefined): ComparatorResult => {
    if (!a || !b) {
      return a === b ? 0 : a ? -1 : 1;
    }
    const folderDiff = makeFolderComparator(getSpaceFn)(getFolderFn(a.folderDuid), getFolderFn(b.folderDuid));
    if (folderDiff !== 0) {
      return folderDiff;
    }
    return stringComparator(a.order, b.order);
  };

export const makeTaskDocRelationshipDocComparator =
  (
    getSpaceFn: (spaceDuid: string) => Space | undefined,
    getFolderFn: (folderDuid: string) => Folder | undefined,
    getDocFn: (docDuid: string) => Doc | undefined
  ) =>
  (a: TaskDocRelationship, b: TaskDocRelationship): ComparatorResult =>
    makeDocComparator(getSpaceFn, getFolderFn)(getDocFn(a.docDuid), getDocFn(b.docDuid));

export const makeTaskDocRelationshipTaskComparator =
  (
    getSpaceFn: (spaceDuid: string) => Space | undefined,
    getDartboardFn: (dartboardDuid: string) => Dartboard | undefined,
    getTaskFn: (taskDuid: string) => Task | undefined
  ) =>
  (a: TaskDocRelationship, b: TaskDocRelationship): ComparatorResult =>
    makeTaskComparator(getSpaceFn, getDartboardFn)(getTaskFn(a.taskDuid), getTaskFn(b.taskDuid));

export const sortArrayByHash = <T>(arr: T[], key: keyof T): T[] =>
  [...arr].sort(makeStringComparator((e) => simpleStrHash(e[key] as string)));

export const makePropertyComparator = (propertyOrderDuids: string[]) =>
  makeWithUndefinedLast((a: string, b: string) => sign(propertyOrderDuids.indexOf(a) - propertyOrderDuids.indexOf(b)));

export const positionComparator = (a: Position | undefined, b: Position | undefined): ComparatorResult =>
  a === b ? 0 : a === "left" || b === "right" ? -1 : 1;

export const chatComparator = (a: Chat, b: Chat): ComparatorResult =>
  booleanComparator(a.pinned, b.pinned) || stringComparator(b.updatedAt, a.updatedAt);

export const scheduledRunComparator = (a: Chat, b: Chat): ComparatorResult =>
  stringComparator(b.createdAt, a.createdAt);

export const chatMessageComparator = (a: ChatMessage, b: ChatMessage): ComparatorResult =>
  stringComparator(a.createdAt, b.createdAt);

export const relationshipKindKindComparator = (a: RelationshipKindKind, b: RelationshipKindKind): ComparatorResult =>
  sign(RELATIONSHIP_KIND_KIND_SORT_ORDER.indexOf(a) - RELATIONSHIP_KIND_KIND_SORT_ORDER.indexOf(b));

export const relationshipKindComparator = (a: RelationshipKind, b: RelationshipKind): ComparatorResult => {
  const kindDiff = relationshipKindKindComparator(a.kind, b.kind);
  if (kindDiff !== 0) {
    return kindDiff;
  }
  return stringComparator(a.forwardText, b.forwardText);
};

export const layoutComparator = (a: Layout, b: Layout): ComparatorResult => stringComparator(a.createdAt, b.createdAt);

export const commentComparator = makeStringComparator((e: Comment) => e.publishedAt ?? e.updatedAt);

export const skillComparator = (a: Skill, b: Skill): ComparatorResult => stringComparator(a.order, b.order);

export const agentMemoryComparator = (a: AgentMemory, b: AgentMemory): ComparatorResult =>
  stringComparator(b.updatedAt, a.updatedAt);

export const agentScheduleComparator = (a: AgentSchedule, b: AgentSchedule): ComparatorResult =>
  stringComparator(a.createdAt, b.createdAt) || stringComparator(a.duid, b.duid);
